import React, { useState, useEffect, useRef } from 'react';
import { Button, Drawer, Tag, Tabs, Spin, Image } from 'antd';
import { CloseOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { shouldUseKlarityUi, formatUTCTime } from 'utils/common';
import PreviewDetail from 'pages/dashboard/TextVisitPage/Dashboard/components/PreviewDetail';
import AIRichText from 'components/AIRichText';
import { EAIRichTextEditorType } from 'types/ai';
import { TextVisitAttachment, TextVisitServiceType, IPaymentStatus } from 'types/textVisit';
import { sendFollowUp } from 'api/textVisit';
import { gpSendFollowUp } from 'api/group';
import useGetCaseDetail from 'hooks/textVisit/useGetCaseDetail';
import { getTimeLeft, getRefillServiceTitle } from 'pages/dashboard/TextVisitPage/utils';
// merged into single icons import above
import dayjs from 'utils/dayjs';
import s from './s.module.less';
import Refund from 'components/Refund';
import PrescriptionCard from 'components/PrescriptionCard';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import { FileUploadResult } from 'hooks/useFileUpload';
import { isImage } from 'components/Prescriptions/components/AttachFiles';
import { FollowUpServiceTypeMap } from 'pages/dashboard/TextVisitPage/Dashboard/components/MyCaseCard';
import HistoryMessage from 'pages/dashboard/TextVisitPage/Dashboard/components/HistoryMessage';
import AddendumsSection from 'components/VideoVisitSubmittedDrawer/AddendumsSection';
import ProviderStore from 'store/Provider';
import { getProviderNameDisplay } from 'utils/provider';
import usePrivateFilePreview from 'hooks/usePrivateFilePreview';
import { sanitizeRichHtml } from 'utils/sanitize';

interface TextCaseModalProps {
    open: boolean;
    onClose: () => void;
    isOps?: boolean;
    textVisitUuid?: string;
    refundRequestId?: number | null;
    refundStatus?: string | null;
    onRefundSubmitted?: (action: 'next' | 'home') => void;
    isLastPendingReview?: boolean; // whether this is the last pending review request
    hasRefundPermission?: boolean; // whether user has permission to review refund
    showChatMessage?: boolean;
    isGroupPractice?: boolean;
    overrideProviderId?: number | string;
}

const TextVisitSubmittedDrawer: React.FC<TextCaseModalProps> = ({
    open,
    onClose,
    isOps = false,
    textVisitUuid,
    refundRequestId,
    refundStatus,
    onRefundSubmitted,
    isLastPendingReview = false,
    hasRefundPermission,
    showChatMessage,
    isGroupPractice,
    overrideProviderId,
}) => {
    const [caseData, loading, fetchData] = useGetCaseDetail({
        caseUuid: textVisitUuid,
        isOps,
        overrideProviderId,
    });
    const isShowKlarityUi = shouldUseKlarityUi();
    const [attachments, setAttachments] = useState<FileUploadResult[]>([]);
    const [activeTab, setActiveTab] = useState<'follow-ups' | 'treatment' | 'details' | 'messages'>('follow-ups');
    const [followUpMessage, setFollowUpMessage] = useState<string>('');
    const [isSendingFollowUp, setIsSendingFollowUp] = useState(false);
    const [clearMessageFlag, setClearMessageFlag] = useState<{}>();
    const followUpContainerRef = useRef<HTMLDivElement>(null);

    const [getUser] = ProviderStore.useStore();
    const providerName = getProviderNameDisplay(getUser('data'));
    const providerCredentials = getUser('data')?.credential;

    const { previewUrl: providerSignaturePreviewUrl, loading: providerSignatureLoading } = usePrivateFilePreview(
        caseData?.providerSignature?.signatureFileKey,
    );

    const isFollowUpCase = caseData?.serviceType !== TextVisitServiceType.INITIAL_VISIT;
    const isRefill =
        caseData?.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL ||
        caseData?.serviceType === TextVisitServiceType.MEDICATION_REFILL;
    const refillTitle = getRefillServiceTitle({
        serviceType: caseData?.serviceType,
        specialty: (caseData as { specialty?: string | null })?.specialty ?? null,
        assessmentName: caseData?.assessmentName ?? null,
    });
    const caseTitle = isFollowUpCase
        ? (isRefill ? refillTitle : null) ||
          FollowUpServiceTypeMap[caseData?.serviceType as keyof typeof FollowUpServiceTypeMap]?.title ||
          ''
        : caseData?.assessmentName || '';

    const items = [
        {
            key: 'follow-ups',
            label: 'Follow-ups',
        },
        {
            key: 'treatment',
            label: 'Visit Notes',
        },
        {
            key: 'details',
            label: 'Details',
        },
        ...(showChatMessage
            ? [
                  {
                      key: 'messages',
                      label: 'Messages',
                  },
              ]
            : []),
    ];

    // Keep the drawer visible even while loading data

    const handleSendFollowUp = async () => {
        try {
            setIsSendingFollowUp(true);
            if (!caseData) return;
            const res = isGroupPractice
                ? await gpSendFollowUp({
                      textVisitId: caseData.id,
                      message: followUpMessage,
                  })
                : await sendFollowUp(
                      {
                          textVisitId: caseData.id,
                          message: followUpMessage,
                      },
                      caseData.assignedProviderId ?? undefined,
                  );
            if (!res.error) {
                fetchData(textVisitUuid!);
                setFollowUpMessage('');
                setClearMessageFlag({});
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            setIsSendingFollowUp(false);
        }
    };

    useEffect(() => {
        const fetchAttachmentUrls = async () => {
            try {
                const resAttachments: FileUploadResult[] = await Promise.all(
                    (caseData?.treatmentPlan?.attachments as TextVisitAttachment[])?.map(async (attachment) => {
                        const res = await providerGetKlarityResourcesUrl(attachment.path || '');
                        if (!res.error) {
                            return {
                                path: attachment.path,
                                name: attachment.name,
                                type: attachment.type,
                                previewUrl: res.data?.data?.singedUrl || '',
                            };
                        }
                        return attachment;
                    }),
                );
                setAttachments(resAttachments);
            } catch (error) {
                console.error(error);
            }
        };
        if (caseData?.treatmentPlan?.attachments) {
            fetchAttachmentUrls();
        }
    }, [caseData?.treatmentPlan?.attachments]);

    useEffect(() => {
        if (caseData && !caseData.followUps?.length) {
            setActiveTab('treatment');
        }
    }, [caseData?.followUps?.length]);

    // 自动滚动到最新消息
    useEffect(() => {
        if (activeTab === 'follow-ups' && followUpContainerRef.current && caseData?.followUps?.length && open) {
            // 使用 setTimeout 确保 DOM 已经更新
            setTimeout(() => {
                followUpContainerRef.current?.scrollTo({
                    top: followUpContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }, 100);
        }
    }, [activeTab, caseData?.followUps?.length, open]);

    const renderTreatmentPlan = () => {
        if (!caseData) return null;
        const data = caseData.treatmentPlan;
        if (!data) return null;

        return (
            <div className={s.treatmentPlan}>
                {/* Response Section */}
                <div className={s.section}>
                    <div className={s.sectionTitle}>Response</div>
                    <div className={s.responseContent}>
                        <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(data.treatmentPlan || '') }} />

                        {attachments?.length && attachments.length > 0 ? (
                            <div className={s.attachments}>
                                {attachments.map((attachment, index) => (
                                    <div key={index} className={s.attachmentItem}>
                                        {isImage(attachment) ? (
                                            <div className={s.attachmentPreview}>
                                                <Image
                                                    src={attachment.previewUrl}
                                                    alt={attachment.name}
                                                    className={s.filePreviewImage}
                                                    preview={{ src: attachment.previewUrl }}
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className={s.fileInfo}
                                                onClick={() => window.open(attachment.previewUrl || '', '_blank')}
                                            >
                                                <div className={s.fileName}>{attachment.name || ''}</div>
                                                <div className={s.fileType}>
                                                    {attachment.type
                                                        ? attachment.name?.split('.').pop()?.toUpperCase()
                                                        : 'Unknown type'}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>

                {/* Prescription Section */}
                {caseData.prescriptions && caseData.prescriptions.length > 0 && (
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Prescription</div>
                        <div className={s.prescriptionCards}>
                            {caseData.prescriptions.map((prescription, index) => (
                                <PrescriptionCard key={index} prescription={prescription} />
                            ))}
                        </div>
                    </div>
                )}

                {data.providerNote && (
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Private notes</div>
                        <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(data.providerNote || '') }} />
                    </div>
                )}
                <div style={{ paddingBlockEnd: '1px' }}>
                    <div className={s.providerViewOnlyWrap}>
                        <h4>The following session is not visible to patient</h4>
                        <div className={s.section}>
                            <div className={s.sectionTitle}>Notes for the Klarity billing team (optional)</div>
                            {data.klarityBillingNote ? (
                                <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(data.klarityBillingNote || '') }} />
                            ) : (
                                <div>-</div>
                            )}
                        </div>

                        <div className={s.section}>
                            <div className={s.sectionTitle}>Diagnosis</div>
                            {data.diagnoses && data.diagnoses.length > 0 ? (
                                <ul className={s.diagnosisList}>
                                    {data.diagnoses.map(
                                        (entry: { diagnosisCode?: string; description?: string }, idx: number) => (
                                            <li key={idx}>
                                                {entry.diagnosisCode
                                                    ? `${entry.diagnosisCode} - ${entry.description || ''}`
                                                    : entry.description || '-'}
                                            </li>
                                        ),
                                    )}
                                </ul>
                            ) : (
                                <div>-</div>
                            )}
                        </div>

                        <div className={s.section}>
                            <div className={s.sectionTitle}>Billing codes</div>
                            {data.billingCodes && data.billingCodes.length > 0 ? (
                                <div className={s.billingCodesList}>
                                    <ul className={s.billingCodesUl}>
                                        {data.billingCodes.map(
                                            (
                                                entry: {
                                                    cptCode?: string;
                                                    description?: string;
                                                    unitBasis?: string;
                                                    units?: number;
                                                    insuranceRate?: number;
                                                    isPrimary?: boolean;
                                                },
                                                idx: number,
                                            ) => (
                                                <li key={idx}>
                                                    <div className={s.billingCodeRow}>
                                                        <span className={s.billingCodeName}>
                                                            {entry.cptCode || '-'} — {entry.description || '-'}
                                                        </span>
                                                        <span className={s.billingCodeTag}>
                                                            <Tag
                                                                className={entry.isPrimary ? s.primaryTag : s.addOnTag}
                                                            >
                                                                {entry.isPrimary ? 'Primary' : 'Add-on'}
                                                            </Tag>
                                                        </span>
                                                        <span className={s.billingCodeUnit}>
                                                            {{
                                                                TIMED: 'Timed',
                                                                PER_ITEM_ADMINISTRATION: 'Per item/administration',
                                                                PER_SESSION: 'Per session',
                                                            }[entry.unitBasis || 'PER_SESSION'] || entry.unitBasis}
                                                        </span>
                                                        <span className={s.billingCodeUnit}>
                                                            {entry.units || 1} Unit{(entry.units || 1) > 1 ? 's' : ''}
                                                        </span>
                                                        <span className={s.billingCodeRate}>
                                                            {entry.insuranceRate != null
                                                                ? `$${entry.insuranceRate.toFixed(2)}`
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                    <div className={s.claimTotal}>
                                        Claim total{' '}
                                        <strong>
                                            $
                                            {data.billingCodes
                                                .reduce(
                                                    (sum: number, e: { insuranceRate?: number; units?: number }) =>
                                                        sum + (e.insuranceRate || 0) * (e.units || 1),
                                                    0,
                                                )
                                                .toFixed(2)}
                                        </strong>
                                    </div>
                                </div>
                            ) : (
                                <div>-</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={s.section}>
                    <div className={s.sectionTitle}>Signatures</div>
                    <div className="flex gap-1 flex-col">
                        <img src={providerSignaturePreviewUrl} alt="Provider signature" />
                        <div>
                            Signed by {providerName}, {providerCredentials}
                        </div>
                        <div>{formatUTCTime(caseData.providerTreatmentSubmittedAt || '', 'MM/DD/YYYY, hh:mm A')}</div>
                    </div>
                </div>

                <AddendumsSection treatmentId={data.id} businessId={caseData.id} />
            </div>
        );
    };

    const renderFollowUps = () => {
        if (!caseData) return null;
        const data = caseData.followUps;
        return (
            <div className={s.followUps}>
                <div className={s.followUpItem} ref={followUpContainerRef}>
                    {data?.map((message) => (
                        <div className="flex flex-col gap-2" key={message.id}>
                            <div className="flex gap-3 items-center">
                                {message.senderType === 'PROVIDER' ? (
                                    <>
                                        <div className="text-sm text-gray-500 font-medium">
                                            {caseData.assignedProviderFirstName} {caseData.assignedProviderLastName}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {dayjs.utc(message.createdAt).local().format('MMM D, YYYY, h:mm A')}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-sm text-gray-500 font-medium">
                                            {caseData.patientFirstName} {caseData.patientLastName}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {dayjs.utc(message.createdAt).local().format('MMM D, YYYY, h:mm A')}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className={s.message} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml((message.message || '').replace(/\n/g, '<br />')) }} />
                        </div>
                    ))}
                </div>
                {!isOps && (
                    <div className={s.followUpEditor}>
                        <AIRichText
                            type={EAIRichTextEditorType.TEXT_VISIT_FOLLOW_UP_MESSAGE}
                            relationId={caseData.id}
                            onChange={setFollowUpMessage}
                            clearMessageFlag={clearMessageFlag}
                            placeholder="Write your response to patients..."
                            height="150px"
                        />
                        <Button
                            className={s.sendBtn}
                            onClick={handleSendFollowUp}
                            loading={isSendingFollowUp}
                            disabled={!followUpMessage || !caseData.providerTreatmentSubmitted}
                        >
                            Send
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const renderBillingSections = () => {
        if (!caseData) return null;
        const data = caseData.treatmentPlan;

        return (
            <div style={{ paddingBlockEnd: '1px' }}>
                <div className={s.providerViewOnlyWrap}>
                    <h4>The following session is not visible to patient</h4>
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Notes for the Klarity billing team (optional)</div>
                        {data?.klarityBillingNote ? (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(data.klarityBillingNote) }} />
                        ) : (
                            <div>-</div>
                        )}
                    </div>

                    <div className={s.section}>
                        <div className={s.sectionTitle}>Diagnosis</div>
                        {data?.diagnoses && data.diagnoses.length > 0 ? (
                            <ul className={s.diagnosisList}>
                                {data.diagnoses.map(
                                    (entry: { diagnosisCode?: string; description?: string }, idx: number) => (
                                        <li key={idx}>
                                            {entry.diagnosisCode
                                                ? `${entry.diagnosisCode} - ${entry.description || ''}`
                                                : entry.description || '-'}
                                        </li>
                                    ),
                                )}
                            </ul>
                        ) : (
                            <div>-</div>
                        )}
                    </div>

                    <div className={s.section}>
                        <div className={s.sectionTitle}>Billing codes</div>
                        {data?.billingCodes && data.billingCodes.length > 0 ? (
                            <div className={s.billingCodesList}>
                                <ul className={s.billingCodesUl}>
                                    {data.billingCodes.map(
                                        (
                                            entry: {
                                                cptCode?: string;
                                                description?: string;
                                                unitBasis?: string;
                                                units?: number;
                                                insuranceRate?: number;
                                                isPrimary?: boolean;
                                            },
                                            idx: number,
                                        ) => (
                                            <li key={idx}>
                                                <div className={s.billingCodeRow}>
                                                    <span className={s.billingCodeName}>
                                                        {entry.cptCode || '-'} — {entry.description || '-'}
                                                    </span>
                                                    <span className={s.billingCodeTag}>
                                                        <Tag className={entry.isPrimary ? s.primaryTag : s.addOnTag}>
                                                            {entry.isPrimary ? 'Primary' : 'Add-on'}
                                                        </Tag>
                                                    </span>
                                                    <span className={s.billingCodeUnit}>
                                                        {{
                                                            TIMED: 'Timed',
                                                            PER_ITEM_ADMINISTRATION: 'Per item/administration',
                                                            PER_SESSION: 'Per session',
                                                        }[entry.unitBasis || 'PER_SESSION'] || entry.unitBasis}
                                                    </span>
                                                    <span className={s.billingCodeUnit}>
                                                        {entry.units || 1} Unit{(entry.units || 1) > 1 ? 's' : ''}
                                                    </span>
                                                    <span className={s.billingCodeRate}>
                                                        {entry.insuranceRate != null
                                                            ? `$${entry.insuranceRate.toFixed(2)}`
                                                            : '-'}
                                                    </span>
                                                </div>
                                            </li>
                                        ),
                                    )}
                                </ul>
                                <div className={s.claimTotal}>
                                    Claim total{' '}
                                    <strong>
                                        $
                                        {data.billingCodes
                                            .reduce(
                                                (sum: number, e: { insuranceRate?: number; units?: number }) =>
                                                    sum + (e.insuranceRate || 0) * (e.units || 1),
                                                0,
                                            )
                                            .toFixed(2)}
                                    </strong>
                                </div>
                            </div>
                        ) : (
                            <div>-</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderDetails = () => {
        if (!caseData) return null;
        if (isFollowUpCase) {
            return (
                <>
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Visit reason</div>
                        <div>{caseData.patientAdditionalComment}</div>
                    </div>
                    {renderBillingSections()}
                </>
            );
        }
        return (
            <>
                <PreviewDetail caseData={caseData} showHeader={false} isProviderEligible />
                {renderBillingSections()}
            </>
        );
    };

    const renderMessage = () => {
        return (
            <div>
                <HistoryMessage
                    clientId={caseData?.clientId}
                    providerId={caseData?.assignedProviderId!}
                    isOps
                    caseData={caseData}
                />
            </div>
        );
    };

    return (
        <Drawer
            open={open}
            width={900}
            onClose={onClose}
            placement="right"
            // zIndex={1004}
            rootClassName={`${s.drawer} ${isShowKlarityUi ? s.klarityDrawer : ''}`}
            title={
                <div className={s.header}>
                    <span className={s.headerTitle}>{caseData ? `Text visit: ${caseTitle}` : 'Loading case...'}</span>
                    <div className="flex items-center gap-2">
                        {caseData &&
                            caseData.assignedProviderAt &&
                            caseData.serviceType !== TextVisitServiceType.PRESCRIPTION_REFILL &&
                            caseData.serviceType !== TextVisitServiceType.MEDICATION_REFILL && (
                                <div
                                    className={s.timeLeft}
                                    style={{
                                        backgroundColor: getTimeLeft(caseData.assignedProviderAt || '').color,
                                    }}
                                >
                                    <ClockCircleOutlined />
                                    <span>{getTimeLeft(caseData.assignedProviderAt || '').text}</span>
                                </div>
                            )}
                        <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={s.closeBtn} />
                    </div>
                </div>
            }
            closeIcon={null}
            footer={null}
            destroyOnClose
        >
            <div className={s.content}>
                {!caseData ? (
                    <div className="flex items-center justify-center py-16 align-items-center w-full">
                        <Spin tip="Loading case..." />
                    </div>
                ) : (
                    <div className={s.contentInfo}>
                        {/* Hotfix 2026-05-12: held refill info banner. Informational, not error. */}
                        {caseData.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL &&
                            caseData.noteSendStatus === 'PENDING_PAYMENT' && (
                                <div className={s.heldBanner}>
                                    <ClockCircleOutlined className={s.heldBannerIcon} />
                                    <div className={s.heldBannerBody}>
                                        <div className={s.heldBannerText}>
                                            Visit notes on hold — awaiting patient payment.
                                        </div>
                                        <div className={s.heldBannerSub}>
                                            The patient will see the notes after they pay. No action needed from you.
                                        </div>
                                        <div className={s.heldBannerReason}>
                                            Reason:{' '}
                                            {caseData.noteHoldReason === 'NO_PAYMENT_METHOD'
                                                ? 'patient has no payment method on file'
                                                : caseData.noteHoldReason === 'BILLING_FAILED'
                                                  ? 'payment attempt failed'
                                                  : caseData.noteHoldReason === 'INVOICE_UNPAID'
                                                    ? 'awaiting patient payment'
                                                    : 'payment required'}
                                        </div>
                                    </div>
                                </div>
                            )}
                        <div className={s.title}>
                            [{caseData.patientFirstName} {caseData.patientLastName}] {caseTitle}
                        </div>

                        <div className={s.statusTags}>
                            {caseData.paymentStatus && (
                                <Tag className={s.paidTag}>
                                    {caseData.paymentStatus === IPaymentStatus.SUCCEEDED && 'Paid'}
                                    {caseData.paymentStatus === IPaymentStatus.REFUNDED && 'Refunded'}
                                    {caseData.paymentStatus === IPaymentStatus.REFUNDED_FAILED && 'Refund failed'}
                                    {caseData.paymentStatus === IPaymentStatus.REFUNDED_PROCESSING &&
                                        'Refund processing'}
                                </Tag>
                            )}
                            <Tag className={`${s.tag} ${caseData.idVerified ? s.verifiedTag : s.unverifiedTag}`}>
                                {caseData.idVerified ? 'ID verified' : 'ID missing'}
                            </Tag>
                            <Tag className={`${s.tag} ${caseData.pharmacySelected ? s.verifiedTag : s.unverifiedTag}`}>
                                {caseData.pharmacySelected ? 'Pharmacy selected' : 'Pharmacy missing'}
                            </Tag>
                        </div>

                        <div className={s.timeTable}>
                            <div className={s.tableRow}>
                                <div className={s.tableCell}>
                                    <div className={s.label}>Submitted on</div>
                                    <div className={s.value}>
                                        {formatUTCTime(caseData.patientSubmitAt || '', 'MM/DD/YYYY')}
                                    </div>
                                </div>
                                <div className={s.tableCell}>
                                    <div className={s.label}>Initial response at</div>
                                    <div className={s.value}>
                                        {formatUTCTime(caseData.providerTreatmentSubmittedAt || '', 'MM/DD/YYYY')}
                                    </div>
                                </div>
                                <div className={s.tableCell}>
                                    <div className={s.label}>Last updated</div>
                                    <div className={s.value}>
                                        {formatUTCTime(caseData.updatedAt || '', 'MM/DD/YYYY')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => setActiveTab(key as 'follow-ups' | 'treatment' | 'details' | 'messages')}
                            items={items}
                        />
                        {activeTab === 'follow-ups' && renderFollowUps()}
                        {activeTab === 'treatment' && renderTreatmentPlan()}
                        {activeTab === 'details' && renderDetails()}
                        {activeTab === 'messages' && renderMessage()}
                    </div>
                )}
                {isOps && refundRequestId && caseData && (
                    <div className={s.extraContent}>
                        <Refund
                            refundId={refundRequestId || 0}
                            refundStatus={refundStatus}
                            onSubmitted={onRefundSubmitted}
                            isLastItem={isLastPendingReview}
                            hasRefundPermission={hasRefundPermission}
                        />
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default TextVisitSubmittedDrawer;
